import React from 'react';
import { debounce } from 'lib/Utils';
import { NoBidFilterValue } from './AuctionsListFilters';

const emptyFormData = {
  FolderID: null,
  Number: '',
  Title: '',
  PlateNumber: '',
  VehicleBrand: '',
  VehicleDoors: '',
  VehicleEngine: '',
  VehicleEngineCapacity: '',
  VehicleModel: '',
  VehicleRegistrationYear: '',
};

const useFolderSearching = (
  { auction, folder, folders, onClearSearchFolders, onSearchFolders },
  setFormData
) => {
  const [folderSearchTerm, setFolderSearchTerm] = React.useState(
    auction ? auction.Number : folder ? folder.Number : ''
  );
  const [selectedFolder, setSelectedFolder] = React.useState(null);
  const [showResetModal, setShowResetModal] = React.useState(false);

  // prevent typing in input when folder is selected
  const handleSearchKeyDown = React.useCallback(
    e => {
      if (selectedFolder) {
        e.preventDefault();
        setShowResetModal(true);
        return false;
      }
    },
    [selectedFolder]
  );

  // Intercept clear action in order to show confirmation modal
  const handleSearchChange = React.useCallback(
    e => {
      if (!e.target.value && e.type === 'click' && selectedFolder) {
        setFolderSearchTerm(selectedFolder.value);
        setShowResetModal(true);
      } else {
        setFormData(fd => {
          const validationMessages = { ...fd.validationMessages };
          if (validationMessages.Number && e.target.value)
            validationMessages.Number = null;
          return {
            ...fd,
            data: { ...fd.data, Number: e.target.value },
            validationMessages,
          };
        });
      }
    },
    [setFormData, selectedFolder, setFolderSearchTerm, setShowResetModal]
  );

  const handleResetConfirmation = React.useCallback(() => {
    setSelectedFolder(null);
    setFolderSearchTerm('');
    setShowResetModal(false);
    setFormData(fd => ({
      ...fd,
      data: { ...fd.data, ...emptyFormData },
      validationMessages: { ...fd.validationMessages, ...emptyFormData },
    }));
  }, [setFormData, setSelectedFolder, setFolderSearchTerm, setShowResetModal]);

  const folderOptions = React.useMemo(
    () =>
      (folders || []).map(
        ({ ID, Number, PlateNumber, VehicleBrand, VehicleModel }) => {
          const brand = [VehicleBrand, VehicleModel].filter(p => !!p).join(' ');
          const label = [Number, brand, PlateNumber]
            .filter(p => !!p)
            .join(' | ');
          return {
            key: ID,
            label,
            value: label,
          };
        }
      ),
    [folders]
  );

  // Search callback: when param is at least 3 chars and hasn't been typed for 300ms
  const handleSearch = React.useMemo(
    () =>
      debounce(
        value => value && String(value).length > 2 && onSearchFolders(value),
        300
      ),
    [onSearchFolders]
  );

  // Move folder data into form upon selection
  const handleSelect = React.useCallback(
    (value, option) => {
      setSelectedFolder(option);
      const folder = folders.find(f => f.ID === option.key);
      setFormData(fd => ({
        ...fd,
        data: {
          FolderID: folder.ID,
          Number: folder.Number,
          PlateNumber: folder.PlateNumber,
          Title: folder.Title,
          VehicleBrand: folder.VehicleBrand,
          VehicleDoors: folder.VehicleDoors,
          VehicleEngine: folder.VehicleEngine,
          VehicleEngineCapacity: folder.VehicleEngineCapacity,
          VehicleModel: folder.VehicleModel,
          VehicleRegistrationYear: folder.VehicleRegistrationYear,
        },
        validationMessages: {
          ...fd.validationMessages,
          ...emptyFormData,
        },
      }));
    },
    [folders, setFormData, setSelectedFolder]
  );

  // when input is less than 3 clear results
  React.useEffect(() => {
    const shouldClear =
      !folderSearchTerm || String(folderSearchTerm).length < 3;
    if (shouldClear && folders) onClearSearchFolders();
  }, [folders, folderSearchTerm]); //eslint-disable-line

  return {
    folderOptions,
    folderSearchTerm,
    handleResetConfirmation,
    handleSearch,
    handleSearchChange,
    handleSearchKeyDown,
    handleSelect,
    selectedFolder,
    setFolderSearchTerm,
    setShowResetModal,
    showResetModal,
  };
};

const mapFormDataToApi = ({
  data: {
    CountryID,
    FolderID,
    ExpertID,
    InsuranceID,
    Number,
    PartDescription,
    PartNumber,
    PartPrice,
    PartTypeID,
    PlateNumber,
    Quantity,
    RegionID,
    VehicleBrand,
    VehicleDoors,
    VehicleEngine,
    VehicleEngineCapacity,
    VehicleModel,
    VehicleRegistrationYear,
    Title,
  },
  garage: garageData,
}) => {
  return {
    auctionData: {
      CountryID,
      PartDescription,
      PartNumber,
      PartPrice,
      PartTypeID,
      Quantity,
      RegionID,
    },
    folderData: {
      ExpertID,
      ID: FolderID || undefined,
      InsuranceID,
      Number,
      PlateNumber,
      Title,
      VehicleBrand,
      VehicleDoors,
      VehicleEngine,
      VehicleEngineCapacity,
      VehicleModel,
      VehicleRegistrationYear,
    },
    garageData,
  };
};

/**
 * Supplies filtered exprerts if insurance and insurances if is expert
 * @param {object} props - component props
 * @param {object} partyFlags - usePartyFlags hook result
 */
const useInsurancesAndExperts = (
  { experts, insurances, user },
  { isInsurance, isExpert }
) => {
  const expertInsurances = React.useMemo(
    () =>
      isExpert && Array.isArray(insurances) && user
        ? insurances.filter(insurance =>
            user.ExpertInsuranceIDs.find(insID => insID === insurance.ID)
          )
        : [],
    [isExpert, insurances, user]
  );

  const insuranceExperts = React.useMemo(
    () =>
      isInsurance && Array.isArray(experts) && user
        ? experts.filter(expert =>
            user.InsuranceExpertIDs.find(expID => expID === expert.ID)
          )
        : [],
    [isInsurance, experts, user]
  );

  return { expertInsurances, insuranceExperts };
};

const formatBidTypes = filters => {
  const { Value: partTypeFilters } =
    filters.find(f => f.Name === 'BidTypes') || {};
  console.log('filters', filters, '->', partTypeFilters);
  if (!Array.isArray(partTypeFilters) || partTypeFilters.length === 0)
    return null;

  return partTypeFilters.reduce((obj, type) => {
    if (type !== NoBidFilterValue) obj[type] = true;
    return obj;
  }, {});
};

export {
  formatBidTypes,
  mapFormDataToApi,
  useFolderSearching,
  useInsurancesAndExperts,
};
